<template>
  <div
    v-click-outside="closeDropdown"
    class="analyses-hero-search position-relative"
    data-qa="analyses_hero_search"
  >
    <CoreSearchField
      :value="query"
      maxlength="150"
      autocomplete="off"
      :placeholder="SEARCH_PLACEHOLDER"
      clear-icon="ui-icon-close-not-a-circle"
      background-color="bg-gray-0"
      :readonly="isMobile"
      data-qa="analyses_hero_search_input"
      :debounce-ms="300"
      @input="onInput"
      @input:debounced="onDebouncedInput"
      @focus="onFocus"
      @click="onTriggerClick"
      @keydown.enter.prevent="goToFindPage"
    />

    <div
      v-if="!isMobile && isDropdownVisible"
      class="analyses-hero-search__dropdown bg-gray-0 rounded-sm"
      :class="{ 'analyses-hero-search__dropdown_empty': isDropdownEmpty }"
      data-qa="analyses_hero_search_dropdown"
    >
      <AnalysesHeroSearchResults
        :query="query"
        :results="results"
        :is-loading="isLoading"
        :show-empty="!isLoading"
      />
    </div>

    <CoreDialog
      v-if="isMobile"
      v-model="isMobileDialogOpen"
      size="fullscreen"
      title="Поиск анализа"
      divider
      data-qa="analyses_hero_search_dialog"
    >
      <template #prependBtn>
        <v-icon
          class="cursor-pointer"
          data-qa="analyses_hero_search_dialog_close"
          @click="closeMobileDialog"
        >
          ui-icon-close-not-a-circle
        </v-icon>
      </template>

      <template #extension>
        <div class="mx-n2">
          <CoreSearchField
            :value="query"
            maxlength="150"
            autocomplete="off"
            :placeholder="SEARCH_PLACEHOLDER"
            clear-icon="ui-icon-close-not-a-circle"
            autofocus
            data-qa="analyses_hero_search_dialog_input"
            :debounce-ms="300"
            @input="onInput"
            @input:debounced="onDebouncedInput"
            @keydown.enter.prevent="goToFindPage"
          />
        </div>
      </template>

      <template #default="{ counterXSpaceClass }">
        <AnalysesHeroSearchResults
          :class="[counterXSpaceClass, 'mt-n4']"
          :query="query"
          :results="results"
          :is-loading="isLoading"
          :show-empty="isQueryReady && !isLoading"
        />
      </template>
    </CoreDialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import redirect from 'src/utils/redirect'
import { useContextStore, useURLStore } from 'src/store'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import CoreSearchField from 'src/core/CoreSearchField/CoreSearchField.vue'
import AnalysesHeroSearchResults from 'src/components/pages/[town-translit]/diagnostika/analiz/components/AnalysesHeroSearchResults.vue'
import apiAnalysesSearch from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesSearch'
import type { AnalysesSearchResponse } from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesSearch.types'
import { SEARCH_PLACEHOLDER } from 'src/components/pages/[town-translit]/diagnostika/analiz/_constants/analyses-search'

const MIN_QUERY_LENGTH = 2

const { context } = useContextStore()
const { reverseFull, urls, patch } = useURLStore()
const isMobile = context.flavour === 'mobile'
const townTranslit = context.town?.translit || ''

if (process.env.IS_SERVER) {
  patch({ find: urls.find })
}

const query = ref('')
const isLoading = ref(false)
const isDropdownOpen = ref(false)
const isMobileDialogOpen = ref(false)
const results = ref<AnalysesSearchResponse>([])

let requestId = 0

let ignoreMobileOpenUntil = 0

const isQueryReady = computed(() => query.value.trim().length >= MIN_QUERY_LENGTH)

const hasResults = computed(() => results.value.length > 0)

const isDropdownVisible = computed(() => (
  isDropdownOpen.value
  && isQueryReady.value
))

const isDropdownEmpty = computed(() => (
  isDropdownVisible.value
  && !isLoading.value
  && !hasResults.value
))

watch(isMobileDialogOpen, isOpen => {
  if (isOpen || !process.env.IS_CLIENT) {
    return
  }

  ignoreMobileOpenUntil = Date.now() + 300

  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }
})

const resetResults = () => {
  results.value = []
}

const onInput = (value: string) => {
  query.value = value

  const trimmed = value.trim()

  if (trimmed.length < MIN_QUERY_LENGTH) {
    isDropdownOpen.value = false
    resetResults()
    isLoading.value = false
    return
  }

  isLoading.value = true
}

const openMobileDialog = () => {
  if (!process.env.IS_CLIENT || isMobileDialogOpen.value) {
    return
  }

  if (Date.now() < ignoreMobileOpenUntil) {
    return
  }

  window.setTimeout(() => {
    if (isMobileDialogOpen.value || Date.now() < ignoreMobileOpenUntil) {
      return
    }

    isMobileDialogOpen.value = true
  }, 0)
}

const onFocus = () => {
  if (isMobile) {
    return
  }

  if (isQueryReady.value && hasResults.value) {
    isDropdownOpen.value = true
  }
}

const onTriggerClick = () => {
  if (isMobile) {
    openMobileDialog()
  }
}

const closeDropdown = () => {
  isDropdownOpen.value = false
}

const closeMobileDialog = () => {
  isMobileDialogOpen.value = false
}

const goToFindPage = () => {
  const trimmed = query.value.trim()

  if (!trimmed || !process.env.IS_CLIENT) {
    return
  }

  const searchUrl = reverseFull({
    name: 'find',
    args: [townTranslit],
  })
  const params = new URLSearchParams({ q: trimmed })

  redirect({
    path: `${searchUrl}?${params.toString()}`,
  })
}

const onDebouncedInput = async (value: string) => {
  const trimmed = value.trim()

  if (trimmed.length < MIN_QUERY_LENGTH) {
    return
  }

  const currentRequestId = requestId + 1

  requestId = currentRequestId
  isLoading.value = true

  if (!isMobile) {
    isDropdownOpen.value = true
  }

  try {
    const data = await apiAnalysesSearch.post({
      query: trimmed,
      town: townTranslit,
    })

    if (currentRequestId !== requestId) {
      return
    }

    results.value = data
  } catch {
    if (currentRequestId !== requestId) {
      return
    }

    resetResults()
  } finally {
    if (currentRequestId === requestId) {
      isLoading.value = false
    }
  }
}
</script>

<style lang="scss" scoped>
.analyses-hero-search {
  &__dropdown {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    left: 0;
    z-index: 2;
    height: 300px;
    overflow-y: auto;
    box-shadow:
      0 5px 5px -3px rgba(0, 0, 0, 0.2),
      0 8px 10px 1px rgba(0, 0, 0, 0.14),
      0 3px 14px 2px rgba(0, 0, 0, 0.12);

    &_empty {
      height: auto;
    }
  }
}
</style>
